/**
 * Commitment terms + resource claims — pure judgment library.
 *
 * PRD: docs/9-exploration/PRD-scorecard-to-strongest.md (WS-A, WS-B1).
 * Schema + RPCs: supabase/migrations/680_plan_commitment_terms.sql,
 *                supabase/migrations/681_plan_resource_claims.sql.
 *
 * Pure on purpose (cutReasons.ts precedent): no React, no Supabase, no
 * stores. The database enforces the state machine authoritatively inside the
 * RPCs; this module mirrors it so UI can disable illegal actions before the
 * round-trip, and centralizes the judgment calls the read models deliberately
 * leave to the client (overdue rendering, divergence tolerance).
 *
 * Binding language rule (L1 + plain-language rule): every user-facing string
 * here says what happened in plain words. No "RPC", no "basis_state", no
 * internal jargon. And no invented specificity: when the data doesn't say,
 * the string doesn't either.
 */

export type TermKind = 'kill_criterion' | 'review_trigger' | 'expiry';
export type TermStatus = 'armed' | 'tripped' | 'resolved' | 'withdrawn';
export type TermResolution = 'stop' | 'rescope' | 'keep';

export type ClaimKind = 'capacity_hours' | 'funding';
export type ClaimStatus = 'declared' | 'superseded' | 'withdrawn';

/** Mirrors the transitions the RPCs accept — nothing else is legal. */
const LEGAL_TERM_TRANSITIONS: Record<TermStatus, readonly TermStatus[]> = {
  armed: ['tripped', 'resolved', 'withdrawn'],
  tripped: ['resolved'],
  resolved: [],
  withdrawn: [],
};

export function canTransitionTerm(from: TermStatus, to: TermStatus): boolean {
  return LEGAL_TERM_TRANSITIONS[from]?.includes(to) ?? false;
}

/** A term is open while a human decision about it is still possible. */
export function isTermOpen(status: TermStatus): boolean {
  return status === 'armed' || status === 'tripped';
}

/**
 * Overdue = armed past its review date. The date itself is the fact; overdue
 * is never stored (the read model computes it the same way). `today` is
 * injected so rendering is testable and timezone handling stays the caller's
 * explicit choice.
 */
export function isTermOverdue(
  status: TermStatus,
  reviewBy: string | null | undefined,
  today: string
): boolean {
  if (status !== 'armed' || !reviewBy) return false;
  return reviewBy < today;
}

export const TERM_KIND_LABELS: Record<TermKind, string> = {
  kill_criterion: 'Kill criterion',
  review_trigger: 'Review trigger',
  expiry: 'Expiry',
};

export interface TermStatusView {
  /** Short label for chips: "Armed", "Tripped", "Stopped", "Kept", … */
  label: string;
  /** One plain sentence for detail rows. */
  sentence: string;
  /** Whether this state needs a human decision now. */
  needsDecision: boolean;
}

export interface TermForDisplay {
  status: TermStatus;
  resolution?: TermResolution | null;
  reviewBy?: string | null;
  trippedBasis?: string | null;
  resolutionBasis?: string | null;
  withdrawnBasis?: string | null;
}

const RESOLUTION_LABELS: Record<TermResolution, string> = {
  stop: 'Stopped',
  rescope: 'Rescoped',
  keep: 'Kept',
};

export function describeTermStatus(term: TermForDisplay, today: string): TermStatusView {
  if (term.status === 'tripped') {
    return {
      label: 'Tripped',
      sentence: term.trippedBasis
        ? `Condition met — ${term.trippedBasis}. Awaiting a decision.`
        : 'Condition met. Awaiting a decision.',
      needsDecision: true,
    };
  }
  if (term.status === 'resolved') {
    const label = term.resolution ? RESOLUTION_LABELS[term.resolution] : 'Resolved';
    return {
      label,
      sentence: term.resolutionBasis ? `${label} — ${term.resolutionBasis}` : label,
      needsDecision: false,
    };
  }
  if (term.status === 'withdrawn') {
    return {
      label: 'Withdrawn',
      sentence: term.withdrawnBasis
        ? `Withdrawn — ${term.withdrawnBasis}`
        : 'Withdrawn from the record.',
      needsDecision: false,
    };
  }
  // armed
  if (isTermOverdue(term.status, term.reviewBy, today)) {
    return {
      label: 'Past review date',
      sentence: `The review date (${term.reviewBy}) has passed without a decision.`,
      needsDecision: true,
    };
  }
  return {
    label: 'Armed',
    sentence: term.reviewBy ? `Armed — review by ${term.reviewBy}.` : 'Armed.',
    needsDecision: false,
  };
}

// ============================================================================
// Follow-through judgment (claims vs live allocation)
// ============================================================================

/**
 * Fraction of the declared value the live number may drift before we call it
 * a divergence. 5% — small enough that a real reallocation shows, large
 * enough that engine rounding noise doesn't cry wolf (L1 cuts both ways:
 * false alarms are also dishonest).
 */
export const FOLLOW_THROUGH_TOLERANCE = 0.05;

export type FollowThroughState =
  | 'no_basis'   // nothing live to compare against — say so, never guess
  | 'matched'    // live allocation within tolerance of the declaration
  | 'under'      // resources fell below what was declared
  | 'over';      // resources exceed what was declared

export interface FollowThroughInput {
  /** basis_state from the read model: 'compared' | 'no_basis' | 'inactive'. */
  basisState: string | null | undefined;
  claimKind: ClaimKind;
  declaredHours?: number | null;
  allocatedHoursLive?: number | null;
  declaredAmount?: number | null;
  envelopeApprovedTotal?: number | null;
}

export interface FollowThroughView {
  state: FollowThroughState;
  /** One plain sentence. */
  sentence: string;
}

export function judgeFollowThrough(
  input: FollowThroughInput,
  tolerance: number = FOLLOW_THROUGH_TOLERANCE
): FollowThroughView {
  if (input.basisState !== 'compared') {
    return {
      state: 'no_basis',
      sentence: 'No live number to compare against yet.',
    };
  }

  const declared =
    input.claimKind === 'capacity_hours' ? input.declaredHours : input.declaredAmount;
  const live =
    input.claimKind === 'capacity_hours'
      ? input.allocatedHoursLive
      : input.envelopeApprovedTotal;

  if (declared == null || live == null || declared <= 0) {
    return {
      state: 'no_basis',
      sentence: 'No live number to compare against yet.',
    };
  }

  const unit = input.claimKind === 'capacity_hours' ? 'hours' : 'approved funding';
  const delta = live - declared;
  if (Math.abs(delta) <= declared * tolerance) {
    return {
      state: 'matched',
      sentence: `Live ${unit} match what was declared.`,
    };
  }
  if (delta < 0) {
    return {
      state: 'under',
      sentence: `Live ${unit} are below what was declared (${formatNumber(live)} vs ${formatNumber(declared)}).`,
    };
  }
  return {
    state: 'over',
    sentence: `Live ${unit} exceed what was declared (${formatNumber(live)} vs ${formatNumber(declared)}).`,
  };
}

function formatNumber(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// ============================================================================
// R-1 terms coverage (ruling 2026-08-24: self-healing gate, not a hard block)
// ============================================================================

export interface TermCoverageRecord {
  itemId: string;
  status: TermStatus;
  reviewBy: string | null;
}

/**
 * R-1: a commitment is covered when it carries at least one OPEN term
 * (armed or tripped) with a review_by date — approval without expiry is
 * accumulation by default. Returns the item ids that are NOT covered.
 * Pure: the caller supplies the rows; the gate machinery decides what a
 * failure means (attach terms, or record a terms_coverage_override artifact).
 */
export function uncoveredCommitmentItems(
  itemIds: readonly string[],
  terms: readonly TermCoverageRecord[]
): string[] {
  const covered = new Set(
    terms
      .filter((t) => isTermOpen(t.status) && t.reviewBy != null && t.reviewBy !== '')
      .map((t) => t.itemId)
  );
  return itemIds.filter((id) => !covered.has(id));
}
